import React from 'react';
import ThumbsSelector from './ThumbsSelector';
import ErrorDisplay, { hasErrors, getErrors } from '../Common/ErrorDisplay';
import { getTableRowsFromNewStructure } from './config/underwritingTableConfig';
import { UNDERWRITING_CONFIG } from './config/underwritingFeedbackConfig';
import './UnderwritingFeedbackTable.css';

/**
 * Category to color mapping for new structure
 */
const CATEGORY_COLOR_MAP = {
  red: '#dc3545',
  amber: '#ffc107',
  green: '#28a745'
};


/**
 * Extract response data from cam_output wrapper
 * Only supports new structure - old structure has been sunset
 */
const extractResponseData = (data) => {
  // New structure with cam_output wrapper inside answer
  if (data?.answer?.cam_output?.response_details) {
    return data.answer.cam_output.response_details;
  }
  // Fallback: check if cam_output is at root level
  if (data?.cam_output?.response_details) {
    return data.cam_output.response_details;
  }
  return null;
};

/**
 * Render content based on content_type
 * @param {Array} content - Array of content strings
 * @param {string} contentType - 'bullets', 'text', 'box', 'bold', or 'heading'
 * @returns {JSX.Element}
 */
const renderContent = (content, contentType = 'text') => {
  if (!content || !Array.isArray(content) || content.length === 0) {
    return <span className="no-content">-</span>;
  }

  // Filter out empty strings
  const filteredContent = content.filter(item => item && String(item).trim().length > 0);
  
  if (filteredContent.length === 0) {
    return <span className="no-content">-</span>;
  }

  if (contentType === 'bullets') {
    return (
      <ul className="content-bullets">
        {filteredContent.map((item, index) => (
          <li key={index}>{item}</li>
        ))}
      </ul>
    );
  }

  if (contentType === 'box') {
    return (
      <div className="content-box">
        {filteredContent.map((item, index) => (
          <div key={index} className="box-item">{item}</div>
        ))}
      </div>
    );
  }

  if (contentType === 'bold') {
    return (
      <div className="content-bold">
        {filteredContent.map((item, index) => (
          <div key={index} className="bold-item">{item}</div>
        ))}
      </div>
    );
  }

  if (contentType === 'heading') {
    return (
      <div className="content-heading">
        {filteredContent.map((item, index) => (
          <div key={index} className="heading-item">{item}</div>
        ))}
      </div>
    );
  }

  // Default: text
  return (
    <div className="content-text">
      {filteredContent.map((item, index) => (
        <div key={index} className="text-item">{item}</div>
      ))}
    </div>
  );
};

/**
 * Table View for Underwriting Feedback
 * 
 * Displays underwriting data in tabular format with:
 * - Dynamic columns based on JSON structure
 * - Content rendered as bullets or text based on content_type
 * - Background colors from JSON
 */
const UnderwritingFeedbackTable = ({ 
  data, 
  emailID, 
  jobId, 
  source,
  feedback,
  onFeedbackChange,
  comments,
  onCommentsChange,
  isSubmitting,
  submitError,
  submitSuccess,
  onSubmit,
  isFormValid
}) => {
  // ===== EARLY EXIT VALIDATION CHECKS =====
  
  // 1. Check cam_output status code if present
  if (data?.cam_output) {
    if (data.cam_output._status_code !== 200) {
      return (
        <div className="underwriting-table-container">
          <div className="underwriting-error-message">
            <h3>⚠️ {data.cam_output.message || 'Processing Failed'}</h3>
            <p>Status Code: {data.cam_output._status_code}</p>
          </div>
        </div>
      );
    }
  }
  
  // 2. Check if data is string - display the string content
  if (typeof data === 'string') {
    return (
      <div className="underwriting-table-container">
        <div className="underwriting-message-display">
          <pre>{data}</pre>
        </div>
      </div>
    );
  }

  // 2. Check if data is valid object
  if (!data || typeof data !== 'object') {
    return (
      <div className="underwriting-table-container">
        <div className="underwriting-error-message">
          <h3>⚠️ Data Type Mismatch</h3>
          <p>Expected object or string, received: {typeof data}.</p>
        </div>
      </div>
    );
  }

  // ===== PROCEED WITH NORMAL FLOW =====
  
  // Extract response data from wrapper
  const responseData = extractResponseData(data);
  
  // 3. Check if structure is recognized
  if (!responseData) {
    // Check if cam_output is blank and there are errors to display
    const showErrors = hasErrors(data);
    const errors = getErrors(data);
    
    if (showErrors) {
      return (
        <div className="underwriting-table-container">
          <div style={{ padding: '20px' }}>
            <ErrorDisplay errors={errors} />
          </div>
        </div>
      );
    }
    
    return (
      <div className="underwriting-table-container">
        <div className="underwriting-error-message">
          <h3>⚠️ Data Type Mismatch</h3>
          <p>ABCD response structure does not match expected format.</p>
          <details>
            <summary>View raw data</summary>
            <pre>{JSON.stringify(data, null, 2)}</pre>
          </details>
        </div>
      </div>
    );
  }
  
  // Get table rows from new structure only (old structure has been sunset)
  const tableRows = getTableRowsFromNewStructure(responseData);

  // 4. Check if table rows are empty
  if (tableRows.length === 0) {
    return (
      <div className="underwriting-table-container">
        <div className="underwriting-error-message">
          <h3>No Data Available</h3>
          <p>No underwriting data found in the response. Please proceed with manual analysis.</p>
        </div>
      </div>
    );
  }

  // Get overall decision data for verdict banner (new structure only)
  const overallDecision = responseData.overall_decision?.value;
  const decisionColor = CATEGORY_COLOR_MAP[responseData.overall_decision?.category] || '#6c757d';
  const backgroundColor = `${decisionColor}1A`; // 10% opacity

  // Filter out feedback-related columns from display
  const HIDDEN_COLUMNS = ['Feedback Needed', 'Feedback Mandatory'];
  
  // Helper function to check if a column header should be hidden
  const isHiddenColumn = (header) => {
    if (Array.isArray(header)) {
      return header.some(h => HIDDEN_COLUMNS.includes(h));
    }
    return HIDDEN_COLUMNS.includes(header);
  };
  
  // Helper function to get display header text
  const getHeaderText = (header) => {
    if (Array.isArray(header)) {
      return header.join(' / ');
    }
    return header;
  };
  
  // Extract unique column headers from all rows (excluding hidden columns)
  const columnHeaders = tableRows.length > 0 && tableRows[0].columns 
    ? tableRows[0].columns
        .filter(col => !isHiddenColumn(col.header))
        .map(col => getHeaderText(col.header)) 
    : [];

  // Internal validation based on actual rendered rows
  const isFormValidInternal = () => {
    // Check all rendered rows with mandatory feedback
    const allFeedbackProvided = tableRows.every(row => {
      if (!row.hasFeedback) return true; // Skip rows without feedback
      if (row.feedbackConfig?.required) {
        const feedbackValue = feedback[row.id];
        // Handle both object format {value, section} and direct value format
        const actualValue = typeof feedbackValue === 'object' ? feedbackValue?.value : feedbackValue;
        return actualValue !== undefined && actualValue !== null;
      }
      return true;
    });
    
    // Check comments field is not empty
    const commentsProvided = comments.trim().length > 0;
    
    return allFeedbackProvided && commentsProvided;
  };

  return (
    <div className="underwriting-table-container">
      {/* Header */}
      <div className="underwriting-table-header">
        <div className="lead-info">
          <span className="timestamp">
            Generated: {new Date(data.timestamp).toLocaleString('en-IN')}
          </span>
        </div>
      </div>

      {/* Verdict Banner */}
      {overallDecision && (
        <div 
          className="verdict-banner"
          style={{
            backgroundColor: backgroundColor,
            borderColor: decisionColor
          }}
        >
          <div className="verdict-label">AI Recommendation</div>
          <div 
            className="verdict-value-box"
            style={{ borderColor: decisionColor, color: decisionColor }}
          >
            {overallDecision}
          </div>
        </div>
      )}

      {/* Scrollable table area */}
      <div className="table-scrollable">
        <table className="underwriting-table">
          <thead>
            <tr>
              {columnHeaders.map((header, index) => (
                <th key={index} className="dynamic-column">{header}</th>
              ))}
              <th className="col-feedback">Feedback</th>
            </tr>
          </thead>
          <tbody>
            {tableRows.map((row) => {
              return (
                <tr 
                  key={row.id}
                  className={`
                    ${row.isToolSection && !row.toolExecuted ? 'tool-not-executed-row' : ''}
                  `}
                >
                  {/* Dynamic Columns - filter out hidden columns */}
                  {row.columns && row.columns
                    .filter(column => !isHiddenColumn(column.header))
                    .map((column, colIndex, filteredColumns) => {
                      // Check if this column is part of a combined section
                      // If the previous column is also combined with the same headers, skip this cell
                      const prevColumn = filteredColumns[colIndex - 1];
                      const isPartOfCombined = column.isCombined && 
                        prevColumn?.isCombined && 
                        JSON.stringify(prevColumn?.combinedHeaders) === JSON.stringify(column?.combinedHeaders);
                      
                      // Skip rendering if this is a continuation of a combined column
                      if (isPartOfCombined) {
                        return null;
                      }
                      
                      // Calculate colSpan for combined columns
                      const colSpan = column.isCombined ? column.combinedSpan : 1;
                      
                      return (
                        <td 
                          key={colIndex} 
                          className="dynamic-column"
                          style={{ backgroundColor: column.background_color || '#FFFFFF' }}
                          colSpan={colSpan}
                        >
                          {renderContent(column.content, column.content_type)}
                          {column.extra_info && column.extra_info.trim() !== '' && (
                            <div className="extra-info">{column.extra_info}</div>
                          )}
                        </td>
                      );
                    })}

                  {/* Feedback Column */}
                  <td className="col-feedback">
                    <div className="feedback-cell">
                      {(() => {
                        // Check if any column has "Feedback Needed" or "Feedback Mandatory" with content "Yes"
                        const feedbackNeededColumn = row.columns?.find(col => 
                          (col.header === 'Feedback Needed' || col.header === 'Feedback Mandatory') &&
                          col.content?.some(item => String(item).trim().toLowerCase() === 'yes')
                        );
                        
                        if (feedbackNeededColumn) {
                          return (
                            <ThumbsSelector
                              value={typeof feedback[row.id] === 'object' ? feedback[row.id]?.value : feedback[row.id]}
                              onChange={(value) => onFeedbackChange(row.id, {
                                value: value,
                                section: row.section,
                                mandatory: false
                              })}
                              required={false}
                            />
                          );
                        }
                        return <span className="no-feedback">-</span>;
                      })()}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* Feedback History Table */}
        {data?.feedback_history?.length > 0 && (
          <div className="feedback-history-section">
            <h3 className="feedback-history-title">User Feedback History for this Case</h3>
            <table className="feedback-history-table">
              <thead>
                <tr>
                  <th>Timestamp</th>
                  <th>Category</th>
                  <th>User</th>
                  <th>Feedback</th>
                </tr>
              </thead>
              <tbody>
                {data.feedback_history.map((entry, index) => (
                  <tr key={index}>
                    <td>{entry.feedback_created_at}</td>
                    <td>{entry.q_label}</td>
                    <td>{entry.user_id}</td>
                    <td>{entry.Value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Fixed bottom bar */}
      <div className="fixed-bottom-bar">
        <input
          type="text"
          className="comments-input"
          value={comments}
          onChange={(e) => onCommentsChange(e.target.value)}
          placeholder={UNDERWRITING_CONFIG.submit.commentsPlaceholder}
        />
        <button
          className="submit-icon-button"
          onClick={onSubmit}
          disabled={!isFormValidInternal() || isSubmitting}
          title="Submit Feedback"
        >
          {isSubmitting ? (
            'Submitting...'
          ) : (
            <>
              <span>Submit</span>
              <span>➤</span>
            </>
          )}
        </button>
      </div>

      {/* Toast notifications */}
      {submitError && (
        <div className="submit-toast error">
          ❌ {submitError}
        </div>
      )}
      
      {submitSuccess && (
        <div className="submit-toast success">
          ✅ Feedback submitted successfully!
        </div>
      )}
    </div>
  );
};

export default UnderwritingFeedbackTable;